import { useConversation, useMessages } from '@restaurant/api-client/react';
import type { MessageDto } from '@restaurant/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ImagePlus, PanelRight, ScrollText } from 'lucide-react';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, Skeleton, Spinner, Tooltip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toastError } from '@/lib/errors';
import { formatPhone } from '@/lib/utils';
import { Composer } from './Composer';
import { MessageBubble } from './MessageBubble';
import { dayLabel, windowState } from './format';
import { firstImage } from './imageAttachment';
import { useFileDrop } from './useFileDrop';

export function ChatView({ conversationId, onTogglePanel }: { conversationId: string; onTogglePanel: () => void }) {
  const { data: c } = useConversation(conversationId);
  const msgs = useMessages(conversationId);
  const { user } = useAuth();
  const users = useQuery({ queryKey: ['users-lite'], queryFn: () => api.users.list().catch(() => []), enabled: user?.role === 'owner' });
  const scroller = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [summary, setSummary] = useState<string | null>(null);
  const [summarizing, setSummarizing] = useState(false);
  const [incoming, setIncoming] = useState<File | null>(null);
  // A picture dropped anywhere on the chat goes to the composer (which checks the 24-hour window).
  const drop = useFileDrop((files) => {
    const img = firstImage(files);
    if (img) setIncoming(img);
    else toast.error('Only pictures can be sent from here');
  });

  const ordered = useMemo(() => [...(msgs.data?.pages.flatMap((p) => p.data) ?? [])].reverse(), [msgs.data]);
  const byWamid = useMemo(() => new Map(ordered.filter((m) => m.wamid).map((m) => [m.wamid!, m])), [ordered]);
  const newestId = ordered[ordered.length - 1]?.id;

  // Mark read on open and whenever a new inbound message arrives while this chat is visible.
  useEffect(() => {
    if (c?.unreadCount && document.hasFocus()) void api.conversations.markRead(conversationId).catch(() => undefined);
  }, [c?.unreadCount, conversationId, newestId]);

  useEffect(() => {
    setSummary(null);
    setIncoming(null);
  }, [conversationId]);

  // Stick to the bottom for new messages (unless the user scrolled up to read history).
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && atBottom) el.scrollTop = el.scrollHeight;
  }, [newestId, atBottom, conversationId]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
    if (el.scrollTop < 120 && msgs.hasNextPage && !msgs.isFetchingNextPage) {
      const prevHeight = el.scrollHeight;
      void msgs.fetchNextPage().then(() => requestAnimationFrame(() => (el.scrollTop = el.scrollHeight - prevHeight)));
    }
  };

  const summarize = async () => {
    setSummarizing(true);
    try {
      setSummary((await api.conversations.summary(conversationId)).summary);
    } catch (err) {
      toastError(err);
    } finally {
      setSummarizing(false);
    }
  };

  if (!c) return <div className="p-4"><Skeleton className="h-10 w-64" /></div>;
  const win = windowState(c.windowOpenUntil);
  const name = c.customer.name ?? formatPhone(c.customer.waId);

  return (
    <div className="relative flex h-full flex-col" {...drop.handlers}>
      {drop.dragging ? (
        <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-background/80 text-primary backdrop-blur-sm">
          <ImagePlus className="size-10" />
          <p className="text-sm font-medium">Drop a picture to send it</p>
        </div>
      ) : null}
      <div className="flex items-center gap-3 border-b bg-card px-3 py-2">
        <Link to="/inbox" className="rounded-md p-1 hover:bg-accent lg:hidden" aria-label="Back">
          <ArrowLeft className="size-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {formatPhone(c.customer.waId)} · <span className={win.open ? 'text-success' : 'text-destructive'}>{win.label}</span>
          </p>
        </div>
        {user?.role === 'owner' && users.data ? (
          <div className="hidden w-40 md:block">
            <Select
              value={c.assignedTo?.id ?? 'none'}
              onValueChange={(v) => void api.conversations.assign(c.id, v === 'none' ? null : v).catch(toastError)}
              options={[{ value: 'none', label: 'Unassigned' }, ...users.data.filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }))]}
            />
          </div>
        ) : c.assignedTo ? (
          <Badge variant="muted">{c.assignedTo.name}</Badge>
        ) : null}
        <Tooltip content="AI summary">
          <Button variant="ghost" size="icon" onClick={() => void summarize()} disabled={summarizing} aria-label="Summarise">
            {summarizing ? <Spinner /> : <ScrollText />}
          </Button>
        </Tooltip>
        <Tooltip content="Customer details">
          <Button variant="ghost" size="icon" onClick={onTogglePanel} aria-label="Customer details">
            <PanelRight />
          </Button>
        </Tooltip>
      </div>
      {summary ? (
        <div className="flex items-start gap-2 border-b bg-accent/40 px-4 py-2 text-sm">
          <ScrollText className="mt-0.5 size-4 shrink-0 text-primary" />
          <p className="flex-1 whitespace-pre-wrap">{summary}</p>
          <button className="text-xs text-muted-foreground hover:underline" onClick={() => setSummary(null)}>Hide</button>
        </div>
      ) : null}
      <div ref={scroller} onScroll={onScroll} className="relative min-h-0 flex-1 overflow-y-auto bg-chat-bg px-3 py-4 md:px-6">
        {msgs.isFetchingNextPage ? <div className="flex justify-center pb-2"><Spinner /></div> : null}
        {msgs.isLoading ? (
          <div className="space-y-3">{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className={`h-10 ${i % 2 ? 'ml-auto w-1/2' : 'w-2/3'}`} />)}</div>
        ) : (
          <div className="mx-auto flex max-w-3xl flex-col gap-1.5">
            {ordered.map((m: MessageDto, i) => {
              const prev = ordered[i - 1];
              const d = new Date(m.timestamp);
              const newDay = !prev || new Date(prev.timestamp).toDateString() !== d.toDateString();
              return (
                <Fragment key={m.id}>
                  {newDay ? (
                    <div className="my-2 flex justify-center">
                      <span className="rounded-full bg-card px-3 py-0.5 text-xs text-muted-foreground shadow-xs">{dayLabel(d)}</span>
                    </div>
                  ) : null}
                  <MessageBubble m={m} quoted={m.replyToWamid ? byWamid.get(m.replyToWamid) : undefined} />
                </Fragment>
              );
            })}
          </div>
        )}
        {!atBottom ? (
          <button
            onClick={() => scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' })}
            className="sticky bottom-2 left-full flex size-9 items-center justify-center rounded-full border bg-card shadow"
            aria-label="Scroll to latest"
          >
            <ChevronDown className="size-4" />
          </button>
        ) : null}
      </div>
      <Composer conversation={c} incoming={incoming} onIncomingHandled={() => setIncoming(null)} />
    </div>
  );
}
